import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPatch, operationsClient } from '@/lib/api';
import { costingSettingsEndpoints } from '@/lib/api/endpoints';
import { useHasPermission } from '@/hooks/usePermissions';

export interface CostingSettings {
  enableReporting: boolean;
  loadedLaborRateDefault: number;
  equipmentBurdenDefault: number;
  varianceAlertLaborPct: number;
  varianceAlertCostPct: number;
  varianceAlertRecipient: CostingVarianceAlertRecipient;
}

export type CostingVarianceAlertRecipient =
  | 'ACCOUNT_OWNER'
  | 'JOB_OWNER'
  | 'ASSIGNED_TECHNICIAN'
  | 'ADMINS'
  | 'NONE';

const costingSettingsKey = ['costing', 'settings'] as const;

export function useCostingSettings(options: { enabled?: boolean } = {}) {
  const canViewContracts = useHasPermission('contracts:view');
  const query = useApiQuery<CostingSettings>({
    queryKey: costingSettingsKey,
    queryFn: () => apiGet<CostingSettings>(operationsClient, costingSettingsEndpoints.get()),
    enabled: canViewContracts && (options.enabled ?? true),
    showErrorToast: false,
  });
  return canViewContracts ? query : { ...query, data: undefined };
}

export function useUpdateCostingSettings() {
  const qc = useQueryClient();
  return useApiMutation<CostingSettings, Partial<CostingSettings>>({
    mutationFn: (patch) => apiPatch(operationsClient, costingSettingsEndpoints.update(), patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: costingSettingsKey });
      toast.success('Costing settings saved');
    },
  });
}
